import React, { useState } from 'react';
import { Trash2, Plus, Search, Pencil } from 'lucide-react';
import { COUNTRIES } from '../constants/countries';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { CustomerModal } from '../components/customers/CustomerModal';
import type { Customer } from '../types/domain';
import type { CreateCustomerInput, UpdateCustomerInput } from '../types/api';
import { useCustomerQuery } from '../hooks/useCustomerQuery';
import { useCustomerMutation } from '../hooks/useCustomerMutation';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGate } from '../components/common/PermissionGate';
import {
  Button,
  Badge,
  Card,
  PageHeader,
  Input,
} from '@license-management/ui';

const CustomerManagement: React.FC = () => {
  usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => Promise<void>>();
  const [confirmMessage, setConfirmMessage] = useState('');

  // Fetch all customers including deleted ones for the counter
  const { 
    customers = [], 
    isLoading: isLoadingCustomers, 
    error: customerError,
    refetch: refetchCustomers
  } = useCustomerQuery({ includeDeleted: true });
  
  // Filter out deleted customers for display
  const activeCustomers = customers.filter(customer => !customer.deletedAt);
  const deletedCount = customers.filter(customer => customer.deletedAt !== null).length;

  const {
    createCustomer,
    updateCustomer,
    deleteCustomer,
    isLoading: isCustomerMutating,
    error: customerMutationError
  } = useCustomerMutation();

  const handleCreateCustomer = async (data: CreateCustomerInput) => {
    try {
      await createCustomer(data);
      setIsModalOpen(false);
    } catch (error) {
      console.error('[CustomerManagement] Error creating customer:', error);
      // Don't close modal on error
    }
  };

  const handleUpdateCustomer = async (data: UpdateCustomerInput) => {
    if (!selectedCustomer) return;
    try {
      await updateCustomer({ 
        id: selectedCustomer.customerId, 
        data 
      });
      setIsModalOpen(false);
    } catch (error) {
      console.error('[CustomerManagement] Error updating customer:', error);
      // Don't close modal on error
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setConfirmMessage(`Are you sure you want to delete customer "${customer.name}"?`);
    setConfirmAction(() => async () => {
      await deleteCustomer(customer.customerId);
      setIsConfirmDialogOpen(false);
    });
    setIsConfirmDialogOpen(true);
  };

  const handleOpenCreateModal = () => {
    setSelectedCustomer(undefined);
    setIsModalOpen(true);
  };


  const filteredCustomers = activeCustomers?.filter(customer => 
    (customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
    (customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) || false)
  ) || [];

  if (isLoadingCustomers) {
    return <LoadingSpinner />;
  }

  const error = customerError || customerMutationError;
  if (error) {
    console.error('[CustomerManagement] Error:', error);
    return (
      <div className="space-y-6">
        <PageHeader
          title="Customers"
          description="Manage customer accounts and information"
          actions={
            <Button onClick={() => refetchCustomers()}>
              Retry Loading
            </Button>
          }
        />
        <Card className="border-red-200 bg-red-50">
          <div className="p-4">
            <h3 className="text-sm font-medium text-red-800">Error loading customers</h3>
            <p className="mt-1 text-sm text-red-700">
              {error instanceof Error ? error.message : String(error)}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        description="Manage customer accounts and information"
        actions={
          <div className="flex items-center gap-3">
            <PermissionGate resource="customers" action="create">
              <Button onClick={handleOpenCreateModal} leftIcon={<Plus className="h-4 w-4" />}>
                Create Customer
              </Button>
            </PermissionGate>
            <Button
              variant="secondary"
              onClick={() => window.location.href = '/customers/trash'}
              leftIcon={<Trash2 className="h-4 w-4" />}
            >
              Recycle Bin
              {deletedCount > 0 && (
                <Badge variant="error" size="sm" className="ml-2">
                  {deletedCount}
                </Badge>
              )}
            </Button>
          </div>
        }
      />

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftElement={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {filteredCustomers.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-zinc-500">No customers found. Try adjusting your search.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-200">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-zinc-200">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.customerId} className="hover:bg-zinc-50 transition-colors">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-zinc-900">
                      {customer.name}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-zinc-600">
                      {customer.email}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-zinc-600">
                      {COUNTRIES.find(c => c.code === customer.country)?.name || customer.country}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <Badge variant={customer.status === 'active' ? 'success' : 'error'}>
                        {customer.status}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGate resource="customers" action="update">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setIsModalOpen(true);
                            }}
                            leftIcon={<Pencil className="h-4 w-4" />}
                          >
                            Edit
                          </Button>
                        </PermissionGate>
                        <PermissionGate resource="customers" action="delete">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteCustomer(customer)}
                          >
                            Delete
                          </Button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <CustomerModal
        isOpen={isModalOpen}
        customer={selectedCustomer}
        onClose={() => setIsModalOpen(false)}
        onSubmit={(data) => {
          if (selectedCustomer) {
            return handleUpdateCustomer(data as UpdateCustomerInput);
          } else {
            return handleCreateCustomer(data as CreateCustomerInput);
          }
        }}
        isSubmitting={isCustomerMutating}
      />
      
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onConfirm={async () => {
          if (confirmAction) {
            await confirmAction();
            setIsConfirmDialogOpen(false);
          }
        }}
        onCancel={() => setIsConfirmDialogOpen(false)}
        title="Delete Customer"
        message={confirmMessage}
      />
    </div>
  );
};

export default CustomerManagement;
